import { Badge, Button, Card, Skeleton, type BadgeProps } from "@etain/ui";
import { AlertCircle, Inbox, Plus, RotateCcw, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { statusLabel, statusTone } from "../lib/format.js";

export interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  children,
  className,
}: PageHeaderProps) {
  return (
    <header className={`page-header${className ? ` ${className}` : ""}`}>
      <div className="page-header__copy">
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {action ? <div className="page-header__action">{action}</div> : null}
      {children}
    </header>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-header">
      <div>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function StatusBadge({ status }: { status?: string | null }) {
  return (
    <Badge tone={statusTone(status) as BadgeProps["tone"]}>
      <span aria-hidden="true" className="status-dot" />
      {statusLabel(status)}
    </Badge>
  );
}

export function LoadingState({ cards = 3 }: { cards?: number }) {
  return (
    <div className="loading-grid" role="status" aria-label="Inhalt wird geladen">
      {Array.from({ length: cards }, (_, index) => (
        <Card className="loading-card" key={index}>
          <Skeleton className="loading-card__title" />
          <Skeleton />
          <Skeleton className="loading-card__short" />
        </Card>
      ))}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message =
    error instanceof Error ? error.message : "Die Daten konnten nicht geladen werden.";
  return (
    <Card className="state-card state-card--error" role="alert">
      <AlertCircle aria-hidden="true" />
      <div>
        <h2>Das hat noch nicht geklappt</h2>
        <p>{message}</p>
      </div>
      {retry ? (
        <Button variant="secondary" onClick={retry}>
          <RotateCcw size={17} aria-hidden="true" /> Erneut versuchen
        </Button>
      ) : null}
    </Card>
  );
}

export function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
  filtered,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  filtered?: boolean;
}) {
  const Icon = filtered ? SearchX : Inbox;
  return (
    <Card className="state-card state-card--empty">
      <span className="state-card__icon">
        <Icon aria-hidden="true" />
      </span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {actionLabel && onAction ? (
        <Button onClick={onAction}>
          <Plus size={17} aria-hidden="true" /> {actionLabel}
        </Button>
      ) : null}
    </Card>
  );
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="filter-bar">{children}</div>;
}

export function MetricCard({
  label,
  value,
  hint,
  icon,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  icon: ReactNode;
  tone?: "default" | "warm" | "danger" | "success";
}) {
  return (
    <Card className={`metric-card metric-card--${tone}`}>
      <div className="metric-card__icon">{icon}</div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        {hint ? <span>{hint}</span> : null}
      </div>
    </Card>
  );
}
